/**
 * Global window.fetch interceptor — installed once in main.tsx.
 *
 * Fixes the same bug at the network layer for every caller at once,
 * including the 26 modules that call fetch('/api/…') directly instead of
 * going through shared/api.ts: when the SWA session cookie has expired,
 * '/api/*' answers with SWA's OWN 401 HTML page, which today ends up
 * rendered verbatim inside a page's error box. A gateway timeout
 * (502/504) hits the same problem with a plain-text body.
 *
 * The decision itself (session-expired vs. timeout vs. "leave it alone")
 * lives in the pure, unit-tested classifyResponseKind() — this file is
 * intentionally a thin wrapper around it. See shared/classifyApiError.js.
 *
 * Zusaetzlich der einzige Zaehlpunkt der Aktivitaetsanzeige (AUFGABE 2):
 * jeder /api/*-Aufruf zaehlt in activityCounter.js hoch/runter, egal ob er
 * ueber react-query oder ein handgeschriebenes fetch() laeuft. end() steht
 * im finally, damit ein Wurf oder ein Abbruch den Zaehler nicht haengen
 * laesst. Siehe shell/Aktivitaetsanzeige.tsx.
 */
import { classifyResponseKind, SESSION_EXPIRED_MESSAGE, TIMEOUT_MESSAGE } from '../shared/classifyApiError'
import { SITZUNG_ABGELAUFEN_EVENT } from '../shared/api'
import { begin, end } from './activityCounter'

function requestPath(input: RequestInfo | URL): string | null {
  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  try {
    const url = new URL(raw, window.location.origin)
    return url.origin === window.location.origin ? url.pathname : null
  } catch {
    return null
  }
}

/**
 * Wraps window.fetch so any same-origin /api/* response that means "session
 * expired" or "gateway timeout" (a) dispatches SITZUNG_ABGELAUFEN_EVENT (§6.59,
 * consumed by the auth providers' SitzungAbgelaufenDialog — only for an
 * expired session, a timeout just gets a readable message) and (b) is
 * replaced by a small synthetic JSON
 * body, so the 26 direct call sites that do res.json()/.text() get a clean
 * message instead of an HTML blob — no page-level changes needed.
 *
 * `/api/staff/*` is excluded: a 401 there is the normal "not logged in yet"
 * state for the kitchen/dishwashing staff portal, not an expired session.
 *
 * Real network failures (offline, DNS, an aborted request) still reject the
 * Promise exactly as before — this only ever transforms a *response*.
 */
export function installSessionFetchInterceptor() {
  const nativeFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const path = requestPath(input)
    const isApiCall = path !== null && path.startsWith('/api/')

    if (isApiCall) begin()
    try {
      const res = await nativeFetch(input, init)

      if (!isApiCall || !path) return res

      const kind = classifyResponseKind({ path, status: res.status, contentType: res.headers.get('content-type') })
      if (!kind) return res

      if (kind === 'session_expired') {
        window.dispatchEvent(new CustomEvent(SITZUNG_ABGELAUFEN_EVENT, { detail: { path } }))
      }

      const message = kind === 'timeout' ? TIMEOUT_MESSAGE : SESSION_EXPIRED_MESSAGE
      return new Response(JSON.stringify({ error: message, code: kind }), {
        // Force a non-2xx status for a classified failure: the origin
        // response could still be a 200 with an HTML body (isHtml branch),
        // and callers everywhere check res.ok, not the body content.
        status: res.ok ? (kind === 'timeout' ? 504 : 401) : res.status,
        headers: { 'content-type': 'application/json' },
      })
    } finally {
      // Zaehlt auch bei einem Wurf (Netzwerkfehler, Abbruch) runter — siehe
      // activityCounter.js: end() faellt nie unter 0.
      if (isApiCall) end()
    }
  }
}
